import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { atom, useAtom } from 'jotai';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';

import type { OrganizationNotificationSettings } from '@/features/NotificationSettings';
import useHandleError from '@/hooks/useHandleError';
import type { User } from '@/pages/Administration/Users';
import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import {
  axiosClient,
  getResponseErrorMessages
} from '@/services/BaseApi.service';
import { userAtom } from '@/store/user.store';
import type { UserInfo } from '@/types/common';

import type {
  NotificationSettings,
  NotificationSettingsFormFields
} from './NotificationSettings.types';

export const orgNotificationSettingsAtom =
  atom<OrganizationNotificationSettings>({
    announcement: true,
    ticket_service: true,
    ticket_threat: true,
    priority_1: true,
    priority_2: true,
    priority_3: true,
    priority_4: true,
    priority_5: true
  });

export const mapUserNotificationSettingsToFormFields = (
  user: UserInfo | User
): NotificationSettingsFormFields => ({
  notification_announcement: user.notification_announcement,
  notification_ticket_service: user.notification_ticket_service,
  notification_ticket_threat: user.notification_ticket_threat,
  notification_ticket_threat_priority_p1:
    user.notification_threat_priority.includes(1),
  notification_ticket_threat_priority_p2:
    user.notification_threat_priority.includes(2),
  notification_ticket_threat_priority_p3:
    user.notification_threat_priority.includes(3),
  notification_ticket_threat_priority_p4:
    user.notification_threat_priority.includes(4),
  notification_ticket_threat_priority_p5:
    user.notification_threat_priority.includes(5),
  notification_tags: user.notification_tags || [],
  notification_ticket_event_create: (
    user.notification_ticket_event || []
  ).includes('CREATE'),
  notification_ticket_event_update: (
    user.notification_ticket_event || []
  ).includes('UPDATE'),
  notification_ticket_event: null, // This field is used for validation only
  notification_report_email: user.notification_report_email,
  notification_report_attachment: user.notification_report_attachment
});

const useValidateOrgNotificationSettings = () => {
  const handleError = useHandleError();
  const [, setOrgNotificationSettings] = useAtom(orgNotificationSettingsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { data, error, isError, isSuccess } = useQuery({
    queryKey: [
      `organizations/${selectedOrganization.label}/check-notifications`
    ],
    enabled: selectedOrganization.label !== 'all'
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch organization notification settings.'
      );
    }
    if (isSuccess) {
      setOrgNotificationSettings(data as OrganizationNotificationSettings);
    }
  }, [data, isError, isSuccess]);
};

export const useFetchNotificationSettings = (userData: UserInfo) => {
  const handleError = useHandleError();
  const { data, error, isError, isFetching, isSuccess } = useQuery({
    queryKey: [`users/${userData.id}`]
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        'Failure to fetch user notification settings.'
      );
    }
  }, [data, isError, isSuccess]);

  const user = (data as UserInfo | undefined) ?? userData;

  return { user, isFetching };
};

const useUpdateNotificationPreferences = () => {
  const [organizations] = useAtom(organizationsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [userData, setUserData] = useAtom(userAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  // TODO: remove this workaround when we get a fix on the backend
  // workaround to prevent an error from displaying when a user tries to save their notification settings and has All Organzations selected
  const selectedOrganizationId = useMemo(() => {
    const firstUserOrg = userData?.organizations[0];
    const firstUserOrgObject = organizations.find(
      (organization) => organization.label === firstUserOrg
    );

    return selectedOrganization.id === 0
      ? firstUserOrgObject?.id
      : selectedOrganization.id;
  }, [organizations, userData]);

  return useMutation({
    mutationFn: (req: NotificationSettings) => {
      return axiosClient.put(
        `/users/organizations/${selectedOrganizationId}/notifications`,
        req
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, getResponseErrorMessages(error));
    },
    onSuccess: (res) => {
      enqueueSnackbar('Notification Preferences Updated', {
        variant: 'success'
      });

      // update userAtom with latest preferences
      setUserData({
        ...userData,
        ...mapUserNotificationSettingsToFormFields(res.data)
      } as UserInfo);

      // refetch data
      queryClient.invalidateQueries({
        queryKey: [
          `organizations/${selectedOrganization.label}/check-notifications`
        ]
      });
    }
  });
};

export { useUpdateNotificationPreferences, useValidateOrgNotificationSettings };
